// frontend/src/components/RowActionsMenu.tsx
import React, { useEffect, useId, useLayoutEffect, useMemo, useRef, useState } from "react";
import { createPortal } from "react-dom";
import { useTranslation } from "react-i18next";

type MenuItem = {
  id: string;
  label: string;
  onClick: () => void;
};

export type RowActionsMenuProps = {
  /** Accessible label for the kebab trigger */
  ariaLabel?: string;
  /** Provide custom items, otherwise default: Edit / Duplicate / Delete (wired via callbacks below) */
  items?: MenuItem[];
  /** Default actions if you don't pass custom items */
  onEdit?: () => void;
  onDuplicate?: () => void;
  onDelete?: () => void;
};

/**
 * RowActionsMenu
 * - Always-visible kebab button.
 * - Menu renders in a portal with position: fixed so it overlays the table and never forces scrollbars.
 * - Auto-flips above if not enough space below; clamps to viewport with 8px padding.
 * - Keyboard: Enter/Space to open; Escape closes; ArrowUp/ArrowDown roving focus.
 */
export function RowActionsMenu({
  ariaLabel,
  items,
  onEdit,
  onDuplicate,
  onDelete,
}: RowActionsMenuProps) {
  const triggerRef = useRef<HTMLButtonElement | null>(null);
  const menuRef = useRef<HTMLDivElement | null>(null);
  const [open, setOpen] = useState(false);
  const [coords, setCoords] = useState<{ left: number; top: number } | null>(null);
  const [activeIndex, setActiveIndex] = useState(0);
  const menuId = useId();
  const { t, i18n } = useTranslation();

  const resolvedAriaLabel = ariaLabel ?? t("actions.open_row_actions");

  const defaultItems: MenuItem[] = useMemo(() => {
    const arr: MenuItem[] = [];
    if (onEdit) arr.push({ id: "edit", label: t("actions.edit"), onClick: onEdit });
    if (onDuplicate) arr.push({ id: "dup", label: t("actions.duplicate"), onClick: onDuplicate });
    if (onDelete) arr.push({ id: "del", label: t("actions.delete"), onClick: onDelete });
    return arr;
  }, [onEdit, onDuplicate, onDelete, i18n.language]);

  const finalItems = items ?? defaultItems;

  function computePosition() {
    const btn = triggerRef.current;
    const panel = menuRef.current;
    if (!btn || !panel) return;

    const r = btn.getBoundingClientRect();
    const viewportW = window.innerWidth;
    const viewportH = window.innerHeight;

    // Temporarily show to measure size (if hidden)
    const prevVis = panel.style.visibility;
    const prevDisplay = panel.style.display;
    panel.style.visibility = "hidden";
    panel.style.display = "block";
    const menuW = panel.offsetWidth;
    const menuH = panel.offsetHeight;
    panel.style.visibility = prevVis;
    panel.style.display = prevDisplay;

    const gap = 8;
    const spaceBelow = viewportH - r.bottom;
    const openBelow = spaceBelow >= menuH + gap;

    // X: right-align to button, clamp to viewport
    let left = Math.min(Math.max(r.right - menuW, 8), viewportW - menuW - 8);
    // Y: below if space, else above; clamp to viewport
    let top = openBelow ? r.bottom + gap : r.top - menuH - gap;
    top = Math.min(Math.max(top, 8), viewportH - menuH - 8);

    setCoords({ left, top });
  }

  function openMenu() { setOpen(true); }
  function closeMenu() { setOpen(false); }

  // Position + focus when opening
  useLayoutEffect(() => {
    if (!open) return;
    computePosition();
    setActiveIndex(0);
  }, [open]);

  // Reposition on scroll/resize while open
  useEffect(() => {
    if (!open) return;
    const onScroll = () => computePosition();
    const onResize = () => computePosition();
    window.addEventListener("scroll", onScroll, true);
    window.addEventListener("resize", onResize);
    return () => {
      window.removeEventListener("scroll", onScroll, true);
      window.removeEventListener("resize", onResize);
    };
  }, [open]);

  // Move focus to the activeIndex item whenever it changes (fixes "sticky" highlight")
  useEffect(() => {
    if (!open) return;
    const items = menuRef.current?.querySelectorAll<HTMLButtonElement>("[role='menuitem']");
    if (!items || !items.length) return;
    items[Math.min(activeIndex, items.length - 1)]?.focus();
  }, [activeIndex, open]);


  // Outside click & Escape to close
  useEffect(() => {
    if (!open) return;
    const onDocMouseDown = (e: MouseEvent) => {
      const targetNode = e.target as Node | null;
      if (!targetNode) return;
      if (triggerRef.current?.contains(targetNode)) return;
      if (menuRef.current?.contains(targetNode)) return;
      closeMenu();
    };
    const onKey = (e: KeyboardEvent) => {
      if (e.key === "Escape") {
        e.preventDefault();
        closeMenu();
        triggerRef.current?.focus();
      }
    };
    document.addEventListener("mousedown", onDocMouseDown);
    document.addEventListener("keydown", onKey);
    return () => {
      document.removeEventListener("mousedown", onDocMouseDown);
      document.removeEventListener("keydown", onKey);
    };
  }, [open]);

  // Keyboard on trigger
  function onTriggerKeyDown(e: React.KeyboardEvent<HTMLButtonElement>) {
    if (e.key === "ArrowDown") {
      e.preventDefault();
      if (!open) openMenu(); else moveFocus(1);
    } else if (e.key === "ArrowUp") {
      e.preventDefault();
      if (!open) openMenu(); else moveFocus(-1);
    } else if (e.key === "Enter" || e.key === " ") {
      e.preventDefault();
      setOpen((o) => !o);
    }
  }

  function moveFocus(delta: number) {
    if (!finalItems.length) return;
    setActiveIndex(i => (i + delta + finalItems.length) % finalItems.length);
  }

  return (
    <div className="relative inline-block" onClick={(e) => e.stopPropagation()}>
      <button
        ref={triggerRef}
        type="button"
        aria-haspopup="menu"
        aria-expanded={open}
        aria-controls={menuId}
        onClick={() => setOpen((o) => !o)}
        onKeyDown={onTriggerKeyDown}
        className="inline-flex items-center justify-center w-7 h-7 rounded bg-gray-200 hover:bg-gray-300 text-slate-800 text-xs transition-colors focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-elani-orange"
        title={t("actions.open_actions")}
      >
        <span className="sr-only">{resolvedAriaLabel}</span>
        <span aria-hidden className="text-lg leading-none -translate-y-[1px]">⋯</span>
      </button>

      {open &&
        createPortal(
          <div
            ref={menuRef}
            id={menuId}
            role="menu"
            className="z-50 rounded-lg border border-slate-200 bg-white p-1 text-xs shadow-[0_10px_30px_rgba(0,0,0,.12),0_2px_8px_rgba(0,0,0,.08)]"
            style={{
              position: "fixed",
              left: coords?.left ?? -9999,
              top: coords?.top ?? -9999,
              minWidth: 160,
            }}
            onClick={(e) => e.stopPropagation()}
          >
            {finalItems.map((it, idx) => (
              <button
                key={it.id}
                role="menuitem"
                type="button"
                className="flex w-full items-center gap-2 rounded-md px-2.5 py-1.5 text-left text-xs text-slate-800 hover:bg-gray-100 focus:bg-gray-100 focus:outline-none transition-colors leading-tight"
                tabIndex={idx === activeIndex ? 0 : -1}
                onMouseEnter={() => setActiveIndex(idx)}
                onClick={() => {
                  closeMenu();
                  it.onClick();
                }}
                onKeyDown={(e) => {
                  if (e.key === "ArrowDown") { e.preventDefault(); moveFocus(1); }
                  else if (e.key === "ArrowUp") { e.preventDefault(); moveFocus(-1); }
                }}
              >
                <span className="inline-block w-4 h-4" aria-hidden />
                <span>{it.label}</span>
              </button>
            ))}
          </div>,
          document.body
        )}
    </div>
  );
}